import { Injectable } from '@angular/core';
import { makeStateKey, TransferState } from '@angular/platform-browser';
import { ContentfulClientApi, Entry, EntryCollection } from 'contentful';
import { defer, from, Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';

import { ContentfulProvider } from './contentful.provider';
import { ContentTypes } from './models/content.types';

/**
 * Base class to handle the generic implementations to load data from contentful.
 */
@Injectable({ providedIn: 'root' })
export abstract class BaseContentfulService {
  client: ContentfulClientApi;

  protected constructor(
    contentfulProvider: ContentfulProvider,
    private state: TransferState
  ) {
    this.client = contentfulProvider.client;
  }

  /**
   * Safe function to load single entry.
   * This method should not throw exception.
   *
   * By default, contentful puts everything in an array.
   * When it's known to be a single entry, it always map
   * in the same way and therefore, this method creates an abstraction
   * for the common usage. This still uses the same base logic to fetch
   * the data from contentful.
   */
  tryLoadEntry<T>(
    brand: string,
    type: ContentTypes,
    query: Record<string, unknown> = {}
  ): Observable<Entry<T> | undefined> {
    return brand && this.client
      ? this.loadEntriesInternal<T>(brand, type, query).pipe(
          map((data) => data?.items[0])
        )
      : of();
  }

  /**
   * Safe function to load entries.
   * This method should not throw exception.
   *
   * It will return undefined when the brand or contentful client is undefined.
   */
  tryLoadEntries<T>(
    brand: string,
    type: ContentTypes,
    query: Record<string, unknown> = {}
  ): Observable<EntryCollection<T> | undefined> {
    return brand && this.client
      ? this.loadEntriesInternal(brand, type, query)
      : of();
  }

  /**
   * Generic public function to load single entry from contentful.
   * This function throws when parameter inputs are invalid.
   */
  loadEntry<T>(
    brand: string,
    type: ContentTypes,
    query: Record<string, unknown> = {}
  ): Observable<Entry<T> | undefined> {
    return brand
      ? this.loadEntriesInternal<T>(brand, type, query).pipe(
          map((data) => data?.items[0])
        )
      : of();
  }

  /**
   * Generic public function to load entries from contentful.
   * This function throws when parameter inputs are invalid.
   */
  loadEntries<T>(
    brand: string,
    type: ContentTypes,
    query: Record<string, unknown> = {}
  ): Observable<EntryCollection<T> | undefined> {
    return this.loadEntriesInternal(brand, type, query);
  }

  /**
   * The main handler to load data from contentful.
   * It also caches the result for transfer state (used on SSR)
   */
  private loadEntriesInternal<T>(
    brand: string,
    type: ContentTypes | string,
    query: Record<string, unknown> = {}
  ): Observable<EntryCollection<T> | undefined> {
    // Todo: Should possibly find a better way, to avoid passing in brand
    const requestKey = makeStateKey<EntryCollection<T>>(
      `${brand}-${type}-${JSON.stringify(query)}`
    );

    if (this.state.hasKey(requestKey)) {
      return of(this.state.get(requestKey, {} as EntryCollection<T>));
    } else {
      return defer(() => {
        return from(
          this.client.getEntries<T>({
            content_type: type,
            ...query,
          })
        );
      }).pipe(
        tap((result) => this.state.set(requestKey, result)),
        catchError(this.handleError),
        shareReplay()
      );
    }
  }

  /** TODO: Use the logger service to log the msg to sumo  */
  private handleError<T>(error: any, _: Observable<EntryCollection<T>>) {
    console.log(error);
    return throwError(error); // caught;
  }
}
